/**
 * Repaginates the collections list when hidden collections
 * (custom.hide_collection) leave pages short of the per-page setting.
 *
 * Fetches the other section pages, rebuilds the ordered list of visible cards,
 * and renders the slice for the current page. After inserting cards, lazy
 * images are activated so placeholder SVGs are replaced with real sources.
 */
(function () {
  var FETCH_BATCH_SIZE = 6;

  function getWrap(list) {
    return list.closest('[data-collection-list-wrap]') || list.parentElement;
  }

  function setLoading(wrap, isLoading) {
    if (!wrap) return;

    if (isLoading) {
      wrap.classList.add('is-loading');
      wrap.setAttribute('aria-busy', 'true');
    } else {
      wrap.classList.remove('is-loading');
      wrap.removeAttribute('aria-busy');
    }
  }

  function init() {
    var list = document.querySelector('[data-collection-list]');
    if (!list) return;

    var wrap = getWrap(list);
    var perPage = parseInt(list.dataset.perPage, 10);
    var currentPage = parseInt(list.dataset.currentPage, 10) || 1;
    var totalPages = parseInt(list.dataset.totalPages, 10) || 1;
    var ownCards = list.querySelectorAll('[data-collection-card]');

    if (!perPage || totalPages <= 1 || (currentPage === 1 && ownCards.length >= perPage)) {
      setLoading(wrap, false);
      return;
    }

    setLoading(wrap, true);

    repaginate(list, perPage, currentPage, totalPages).then(
      function () {
        setLoading(wrap, false);
      },
      function () {
        setLoading(wrap, false);
      }
    );
  }

  function fetchPageCards(sectionId, page) {
    var url = new URL(window.location.href);
    url.searchParams.set('page', page);
    url.searchParams.set('section_id', sectionId);

    return fetch(url.toString()).then(function (response) {
      if (!response.ok) throw new Error('Failed to load page ' + page);
      return response.text();
    }).then(function (html) {
      var doc = new DOMParser().parseFromString(html, 'text/html');
      return Array.from(doc.querySelectorAll('[data-collection-list] [data-collection-card]'));
    });
  }

  async function fetchAllPages(list, currentPage, totalPages) {
    var sectionId = list.dataset.sectionId;
    var pages = new Array(totalPages);
    pages[currentPage - 1] = Array.from(list.querySelectorAll('[data-collection-card]'));

    var pending = [];
    for (var page = 1; page <= totalPages; page += 1) {
      if (page !== currentPage) pending.push(page);
    }

    for (var i = 0; i < pending.length; i += FETCH_BATCH_SIZE) {
      var batch = pending.slice(i, i + FETCH_BATCH_SIZE);
      var results = await Promise.all(batch.map(function (pageNum) {
        return fetchPageCards(sectionId, pageNum);
      }));
      batch.forEach(function (pageNum, index) {
        pages[pageNum - 1] = results[index];
      });
    }

    return pages;
  }

  async function repaginate(list, perPage, currentPage, totalPages) {
    var pages = await fetchAllPages(list, currentPage, totalPages);

    var seen = {};
    var allCards = [];
    pages.forEach(function (cards) {
      (cards || []).forEach(function (card) {
        var id = card.getAttribute('data-collection-id');
        if (id && seen[id]) return;
        if (id) seen[id] = true;
        allCards.push(card);
      });
    });

    var start = (currentPage - 1) * perPage;
    var slice = allCards.slice(start, start + perPage);
    if (!slice.length) return;

    renderSlice(list, slice);
    updatePagination(allCards.length, perPage, currentPage);
  }

  function activateLazyImages(root) {
    root.querySelectorAll('img[data-src], img[data-srcset], source[data-srcset]').forEach(function (el) {
      if (el.dataset.srcset) {
        el.setAttribute('srcset', el.dataset.srcset);
      }
      if (el.dataset.src) {
        el.setAttribute('src', el.dataset.src);
      }
    });
  }

  function adoptCard(card) {
    var node = card.ownerDocument === document ? card : document.importNode(card, true);
    activateLazyImages(node);
    return node;
  }

  function renderSlice(list, slice) {
    var ownCards = Array.from(list.querySelectorAll('[data-collection-card]'));
    var anchor = null;

    if (ownCards.length) {
      anchor = ownCards[ownCards.length - 1].nextSibling;
    } else {
      anchor = list.querySelector('li.col-span-full:last-child');
    }

    ownCards.forEach(function (card) {
      card.remove();
    });

    slice.forEach(function (card) {
      list.insertBefore(adoptCard(card), anchor);
    });

    activateLazyImages(list);

    if (typeof window.initLazyImages === 'function') {
      window.initLazyImages();
    }
  }

  function updatePagination(totalVisible, perPage, currentPage) {
    var pagination = document.querySelector('.pagination');
    if (!pagination) return;

    var newTotal = Math.max(1, Math.ceil(totalVisible / perPage));

    if (newTotal <= 1) {
      var nav = pagination.closest('nav');
      if (nav) nav.remove();
      else pagination.remove();
      return;
    }

    pagination.querySelectorAll('.pagination__page-link, .pagination__page-current').forEach(function (el) {
      var pageNumber = parseInt(el.textContent, 10);
      if (pageNumber && pageNumber > newTotal) {
        var item = el.closest('li');
        if (item) item.remove();
      }
    });

    var position = pagination.querySelector('.pagination__item.text-center');
    if (position) {
      position.textContent = position.textContent.replace(/\d+(\s*)$/, String(newTotal) + '$1');
    }

    var next = pagination.querySelector('.pagination__arrow--next');
    if (next && currentPage >= newTotal) {
      next.removeAttribute('href');
      next.setAttribute('role', 'link');
      next.setAttribute('aria-disabled', 'true');
    }

    var prev = pagination.querySelector('.pagination__arrow--prev');
    if (prev && currentPage <= 1) {
      prev.removeAttribute('href');
      prev.setAttribute('role', 'link');
      prev.setAttribute('aria-disabled', 'true');
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
